import { useEffect, useCallback, useState, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useLocation, useMatch, useNavigate } from 'react-router-dom'
import { useBooleanFlagValue } from '@openfeature/react-sdk'
import { DateTime } from 'luxon'
import { mtc } from '@/styles'
import { useIsDeviceWidth, DeviceWidthCombo } from '@/utils/deviceWidthHelpers'
import { FEATURE_FLAG_KEYS } from '@/flags'
import type { UIMessage } from 'ai'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useMountEffect, useReselector } from '@/utils/sharedHooks'
import { getCurrentUser } from '@/selectors/user.selectors'
import {
  selectPanelOpen,
  selectActiveView,
  selectActiveConversationId,
  selectPendingLaunchContext,
  selectPendingEntryPoint,
  selectActiveLaunchAction,
  selectActiveEntryPoint,
  selectActiveThreadId,
} from './adviser-chat.selectors'
import {
  togglePanel,
  closePanel,
  openPanel,
  setActiveView,
  startNewConversation,
  consumePendingLaunchContext,
  closeMessageThread,
} from '@/features/adviser-chat/adviser-chat.slice'
import { useAdviserChat } from './hooks/useAdviserChat'
import { useConversations } from './hooks/useConversations'
import { useAwaitingReplyCount } from './hooks/useAwaitingReplyCount'
import { isNewUserForSidebarRestructure } from '@/utils/sidebarRolloutHelpers'
import { ChatTrigger } from './components/ChatTrigger'
import { ChatPanel } from './components/ChatPanel'
import { withViewTransition } from '@/features/adviser-chat/components/panelAnimations'
import { ChatView } from './components/ChatView'
import { MessagesLaunchCard } from './components/MessagesLaunchCard'
import { ChatPlaidLauncher } from './bookkeeping/ChatPlaidLauncher'
import { ChatInput, type ChatInputHandle } from './components/ChatInput'
import { ThreadOverlay } from './components/ThreadOverlay'
import { HavenFullScreen } from '@/features/adviser-chat/components/fullscreen/HavenFullScreen'
import { HistoryRail } from '@/features/adviser-chat/components/HistoryRail'
import { ConversationRail } from '@/features/adviser-chat/components/ConversationRail'
import { FullScreenHome } from '@/features/adviser-chat/components/fullscreen/FullScreenHome'
import { ConversationHeader } from '@/features/adviser-chat/components/ConversationHeader'
import { ComposerOwnerBar } from '@/features/adviser-chat/components/ComposerOwnerBar'
import { useOpenHavenFullScreen } from '@/features/adviser-chat/hooks/useOpenHavenFullScreen'
import { useLaunchHaven } from '@/features/adviser-chat/hooks/useLaunchHaven'
import {
  HAVEN_CONVERSATION_PATH,
  HAVEN_FALLBACK_RETURN_PATH,
  INVALID_CONVERSATION_ID,
  havenFullScreenPath,
  isHavenPath,
  parseUrlConversation,
  readHavenReturnTo,
} from '@/features/adviser-chat/lib/havenRoutes'
import { ConversationNotFound } from '@/features/adviser-chat/components/ConversationNotFound'
import type { SendMessage } from './components/messages/ThreadView'
import {
  fetchConversationDetail,
  fetchConversations,
  refreshConversationViews,
  resumeConversation,
} from './adviser-chat.actions'
import { useHavenRelay } from './relay'
import {
  AdviserRole,
  AdviserToolName,
  HAVEN_TITLE,
  MESSAGE_TEAM_CHIP,
  RESOLVED_CASE_STATUS,
} from '@/features/adviser-chat/lib/constants'
import { useAdviserAnalytics } from './analytics'
import {
  getAttachedFiles,
  getMessageContent,
  getToolInvocations,
  restoredMessageParts,
  restoredToolParts,
  sourcesByWidget,
  splitTextAroundWidget,
  widgetCallIdOf,
  toChatRole,
} from './lib/mappers'
import { splitsDrawnText, splitsText } from './components/widgets/registry'
import { isLaunchSentinel } from './lib/launchContext'
import {
  HAVEN_ENTRY_POINT,
  HAVEN_LAUNCH_ACTION,
  type HavenLaunchAction,
  type CitationSource,
  type AdviserMessage,
  type FeedbackVote,
  type ActiveView,
  type ConversationSummary,
  type CustomerCaseEvent,
  type OperatorIdentity,
} from '@/features/adviser-chat/lib/types'

const SAVED_PANEL_KEY = 'havenPanelConversation'

const hasNewerActivity = (
  candidate: ConversationSummary,
  reference: ConversationSummary
): boolean => {
  const candidateReplyAt = DateTime.fromISO(
    candidate.lastActivity?.at ??
      candidate.lastVisibleReplyAt ??
      candidate.updatedAt
  ).toMillis()
  const referenceReplyAt = DateTime.fromISO(
    reference.lastActivity?.at ??
      reference.lastVisibleReplyAt ??
      reference.updatedAt
  ).toMillis()
  if (candidateReplyAt !== referenceReplyAt) {
    return candidateReplyAt > referenceReplyAt
  }
  return (
    DateTime.fromISO(candidate.updatedAt).toMillis() >
    DateTime.fromISO(reference.updatedAt).toMillis()
  )
}

const hasDifferentCase = (
  candidate: ConversationSummary,
  reference: ConversationSummary
): boolean =>
  candidate.case?.id !== reference.case?.id ||
  candidate.case?.status !== reference.case?.status ||
  candidate.case?.waitingOn !== reference.case?.waitingOn ||
  candidate.case?.owner?.friendlyName !== reference.case?.owner?.friendlyName ||
  candidate.case?.owner?.firstName !== reference.case?.owner?.firstName ||
  candidate.case?.owner?.lastName !== reference.case?.owner?.lastName ||
  candidate.case?.owner?.avatarUrl !== reference.case?.owner?.avatarUrl ||
  candidate.case?.returnedToHaven !== reference.case?.returnedToHaven

const toRestoredUiMessage = (message: AdviserMessage): UIMessage => ({
  id: String(message.id),
  role: toChatRole(message.role),
  parts:
    message.role === AdviserRole.Assistant
      ? restoredMessageParts(message)
      : [{ type: 'text', text: message.content }],
  metadata: { restored: true },
})

const isRestoredUiMessage = (message: UIMessage): boolean =>
  typeof message.metadata === 'object' &&
  message.metadata !== null &&
  'restored' in message.metadata &&
  message.metadata.restored === true

const orderRecentMessagesByServer = (
  messages: UIMessage[],
  savedMessages: AdviserMessage[],
  getPersistedIdForClientId: (id: string) => number | undefined
): UIMessage[] => {
  const serverOrder = new Map(
    savedMessages.map((message, index) => [message.id, index])
  )
  const indexed = messages.map((message) => ({
    message,
    order: serverOrder.get(
      isRestoredUiMessage(message)
        ? Number(message.id)
        : (getPersistedIdForClientId(message.id) ?? -1)
    ),
  }))
  const ordered = indexed
    .filter((entry) => entry.order !== undefined)
    .sort((left, right) => (left.order ?? 0) - (right.order ?? 0))
  let next = 0
  return indexed.map(({ message, order }) =>
    order === undefined ? message : ordered[next++].message
  )
}

/** The panel's conversation saved in this tab: an id, `null` for Home, `undefined` when the panel was closed. */
const readSavedPanelConversation = (): number | null | undefined => {
  try {
    const saved = sessionStorage.getItem(SAVED_PANEL_KEY)
    if (saved === null) return undefined
    const id = Number(saved)
    return Number.isInteger(id) && id > 0 ? id : null
  } catch {
    return undefined
  }
}

const savePanelConversation = (conversationId: number | null | undefined) => {
  try {
    if (conversationId === undefined) sessionStorage.removeItem(SAVED_PANEL_KEY)
    else sessionStorage.setItem(SAVED_PANEL_KEY, String(conversationId ?? ''))
  } catch {
    // Storage is unavailable (private mode): a refresh closes the panel, as it always has.
  }
}

export const AdviserChat = () => {
  const dispatch = useAppDispatch()

  const panelOpen = useReselector(selectPanelOpen)
  const activeView = useReselector(selectActiveView)
  const activeConversationId = useReselector(selectActiveConversationId)
  const pendingLaunchContext = useReselector(selectPendingLaunchContext)
  const pendingEntryPoint = useReselector(selectPendingEntryPoint)
  const activeLaunchAction = useReselector(selectActiveLaunchAction)
  const activeEntryPoint = useReselector(selectActiveEntryPoint)
  const activeThreadId = useReselector(selectActiveThreadId)
  const currentUser = useReselector(getCurrentUser)
  const navigate = useNavigate()
  const location = useLocation()
  const isMobile = useIsDeviceWidth(DeviceWidthCombo.mobileOrTablet)
  const onHavenRoute = isHavenPath(location.pathname)
  const isFullScreen = onHavenRoute && !isMobile
  const panelVisible = panelOpen || (onHavenRoute && isMobile)
  const havenVisible = panelVisible || isFullScreen
  const openFullScreen = useOpenHavenFullScreen()
  const launchHaven = useLaunchHaven()

  const {
    trackWidgetViewed,
    trackWidgetClosed,
    trackHistoryOpened,
    trackConversationResumed,
    trackNewConversationClicked,
    trackDeleteConversationClicked,
    trackMessageThreadOpened,
    trackMessageReplySent,
  } = useAdviserAnalytics()
  const wasVisibleRef = useRef(false)

  useEffect(() => {
    if (activeThreadId !== null) trackMessageThreadOpened(activeThreadId)
  }, [activeThreadId, trackMessageThreadOpened])

  const havenCaseHistoryEnabled = useBooleanFlagValue(
    FEATURE_FLAG_KEYS.havenHandToSupport,
    false
  )

  const {
    messages,
    messageCreatedAtByClientId,
    getPersistedIdForClientId,
    isPendingLocalMessage,
    isUnmappedSettledMessage,
    setMessages,
    sendMessage,
    launchWithContext,
    stop,
    isStreaming,
    error,
    clearError,
    retry,
    sourcesMap,
    setSourcesMap,
    feedbackMap,
    setFeedbackMap,
    submitWidgetOutcome,
    startedConversation,
    resumedTurnStored,
    consumeResumedTurn,
    clearPendingResume,
    userTurnsRef,
    operatorAcknowledged,
    operatorHeldRef,
  } = useAdviserChat(havenCaseHistoryEnabled)

  const {
    conversations,
    loaded,
    hasMore,
    load,
    loadMore,
    refresh,
    remove,
    startNew,
    showHistory,
    hideHistory,
  } = useConversations()

  const havenMessagesViewEnabled = useBooleanFlagValue(
    FEATURE_FLAG_KEYS.havenMessagesView,
    false
  )
  const awaitingCount = useAwaitingReplyCount(havenMessagesViewEnabled)

  // Close the panel when any Sheet/Dialog opens (e.g. Add Transaction).
  // Some sheets use local state and never touch Redux, so extraReducers alone
  // is not enough. Watch for any role="dialog" portal being added to <body>.
  useEffect(() => {
    if (!panelVisible || onHavenRoute) return undefined

    const observer = new MutationObserver(() => {
      // Radix popovers carry role="dialog" too, and the chat's dropdowns use them.
      if (
        document.querySelector(
          '[role="dialog"]:not([data-radix-popper-content-wrapper] [role="dialog"])'
        )
      ) {
        dispatch(closePanel())
      }
    })

    observer.observe(document.body, { childList: true, subtree: false })
    return () => observer.disconnect()
  }, [panelVisible, onHavenRoute, dispatch])

  // Load conversations the first time the panel opens
  useEffect(() => {
    if (havenVisible && !loaded && !havenCaseHistoryEnabled) {
      load()
    }
  }, [havenCaseHistoryEnabled, havenVisible, loaded, load])

  useEffect(() => {
    if (
      !havenVisible ||
      !havenCaseHistoryEnabled ||
      activeConversationId === null
    )
      return undefined
    const onFocus = () => {
      dispatch(refreshConversationViews())
    }
    window.addEventListener('focus', onFocus)
    return () => window.removeEventListener('focus', onFocus)
  }, [activeConversationId, dispatch, havenCaseHistoryEnabled, havenVisible])

  useEffect(() => {
    if (havenVisible === wasVisibleRef.current) return
    if (havenVisible) {
      trackWidgetViewed(
        activeEntryPoint || pendingEntryPoint
          ? {
              launchContext:
                activeLaunchAction ??
                (pendingLaunchContext?.type === 'action'
                  ? pendingLaunchContext.action
                  : pendingLaunchContext
                    ? 'message'
                    : null),
              entryPoint: activeEntryPoint ?? pendingEntryPoint,
            }
          : undefined
      )
    } else {
      trackWidgetClosed()
    }
    wasVisibleRef.current = havenVisible
  }, [
    havenVisible,
    trackWidgetViewed,
    trackWidgetClosed,
    activeLaunchAction,
    activeEntryPoint,
    pendingLaunchContext,
    pendingEntryPoint,
  ])

  const latestRestoreRef = useRef(0)
  const chatInputRef = useRef<ChatInputHandle>(null)
  const isStreamingRef = useRef(isStreaming)
  isStreamingRef.current = isStreaming
  const [restoring, setRestoring] = useState(false)
  const [caseEvents, setCaseEvents] = useState<CustomerCaseEvent[]>([])
  const [messagesCursor, setMessagesCursor] = useState<string | null>(null)
  const [caseEventsCursor, setCaseEventsCursor] = useState<string | null>(null)
  const [loadingOlderHistory, setLoadingOlderHistory] = useState(false)
  const [olderHistoryError, setOlderHistoryError] = useState(false)
  const [historyRevision, setHistoryRevision] = useState(0)
  const [restoredMessageDetails, setRestoredMessageDetails] = useState<
    Record<
      string,
      {
        role: `${AdviserRole}`
        createdAt: string
        operator?: OperatorIdentity | null
      }
    >
  >({})
  const [restoredConversation, setRestoredConversation] =
    useState<ConversationSummary>()
  const resetTranscript = useCallback(() => {
    latestRestoreRef.current += 1
    setRestoring(false)
    stop()
    clearError()
    setMessages([])
    setSourcesMap({})
    setFeedbackMap({})
    setCaseEvents([])
    setMessagesCursor(null)
    setCaseEventsCursor(null)
    setLoadingOlderHistory(false)
    setOlderHistoryError(false)
    setHistoryRevision(0)
    setRestoredMessageDetails({})
    setRestoredConversation(undefined)
  }, [stop, clearError, setMessages, setSourcesMap, setFeedbackMap])

  useEffect(() => {
    if (!havenVisible || !pendingLaunchContext) return
    dispatch(consumePendingLaunchContext())
    resetTranscript()
    if (pendingLaunchContext.type === 'message') {
      sendMessage(pendingLaunchContext.text)
    } else {
      launchWithContext(pendingLaunchContext)
    }
  }, [
    havenVisible,
    pendingLaunchContext,
    dispatch,
    sendMessage,
    launchWithContext,
    resetTranscript,
  ])

  const handleTogglePanel = useCallback(() => {
    dispatch(togglePanel())
  }, [dispatch])

  const returnTo =
    readHavenReturnTo(location.state) ?? HAVEN_FALLBACK_RETURN_PATH

  const conversationPathMatch = useMatch(HAVEN_CONVERSATION_PATH)
  const urlConversation = onHavenRoute
    ? parseUrlConversation(conversationPathMatch?.params.conversationId)
    : null

  const navigateToConversation = useCallback(
    (conversationId: number | null, { replace = false } = {}) => {
      navigate(havenFullScreenPath(conversationId), {
        replace,
        state: location.state,
      })
    },
    [location.state, navigate]
  )

  const handleClosePanel = useCallback(() => {
    dispatch(closePanel())
    if (onHavenRoute) navigate(returnTo)
  }, [dispatch, onHavenRoute, navigate, returnTo])

  const handleExitFullScreen = useCallback(() => {
    withViewTransition(() => {
      dispatch(openPanel())
      navigate(returnTo)
    })
  }, [dispatch, navigate, returnTo])

  const handleExpand = useCallback(() => {
    openFullScreen(activeConversationId)
  }, [openFullScreen, activeConversationId])

  const handleSetView = useCallback(
    (view: ActiveView) => {
      if (view === 'history') {
        trackHistoryOpened()
        load()
        showHistory()
      } else {
        hideHistory()
        dispatch(setActiveView(view))
      }
    },
    [dispatch, load, showHistory, hideHistory, trackHistoryOpened]
  )

  // Bumped on every restore so the transcript remounts and opens at the latest message.
  const [restoreCount, setRestoreCount] = useState(0)
  const [conversationNotFound, setConversationNotFound] = useState(false)
  useEffect(() => {
    if (!havenVisible) setConversationNotFound(false)
  }, [havenVisible])

  const showConversationNotFound = useCallback(() => {
    resetTranscript()
    dispatch(startNewConversation())
    setConversationNotFound(true)
  }, [dispatch, resetTranscript])

  const restoreConversation = useCallback(
    async (
      id: number,
      options?: { continuingLaunch?: boolean; trackResume?: boolean }
    ) => {
      const continuingLaunch = options?.continuingLaunch ?? false
      const turnsBefore = userTurnsRef.current
      const restore = ++latestRestoreRef.current
      if (!continuingLaunch) stop()
      // The greeting stays on screen until the history replaces it.
      if (!continuingLaunch) setRestoring(true)
      setConversationNotFound(false)
      if (!continuingLaunch && options?.trackResume !== false)
        trackConversationResumed(id)
      const resumeOptions = {
        ...(continuingLaunch ? { keepLaunch: true } : {}),
        ...(havenCaseHistoryEnabled ? { history: 'recent' as const } : {}),
      }
      const detail = await dispatch(
        resumeConversation(
          id,
          continuingLaunch || havenCaseHistoryEnabled
            ? resumeOptions
            : undefined
        )
      )
      if (restore !== latestRestoreRef.current) return
      // The reload remounts the transcript, so it must not land on a turn or a draft.
      if (
        continuingLaunch &&
        (isStreamingRef.current ||
          userTurnsRef.current !== turnsBefore ||
          chatInputRef.current?.hasDraft())
      )
        return
      if (continuingLaunch) stop()
      if (!detail) {
        if (continuingLaunch) setRestoring(false)
        else showConversationNotFound()
        return
      }
      resetTranscript()
      if (continuingLaunch) clearPendingResume()
      setRestoreCount((count) => count + 1)
      setRestoredConversation(
        havenCaseHistoryEnabled
          ? {
              ...detail.conversation,
              lastVisibleReplyAt: detail.messages.at(-1)?.createdAt ?? null,
            }
          : detail.conversation
      )
      setCaseEvents(detail.caseEvents?.items ?? [])
      const recentHistory =
        havenCaseHistoryEnabled && Boolean(detail.messagesPage)
      setMessagesCursor(
        recentHistory ? (detail.messagesPage?.nextCursor ?? null) : null
      )
      setCaseEventsCursor(
        recentHistory ? (detail.caseEvents?.nextCursor ?? null) : null
      )
      setRestoredMessageDetails(
        Object.fromEntries(
          detail.messages.map((message) => [
            String(message.id),
            {
              role: message.role,
              createdAt: message.createdAt,
              operator: message.operator,
            },
          ])
        )
      )

      if (detail.messages) {
        const uiMessages: UIMessage[] = detail.messages.map(toRestoredUiMessage)
        setMessages(uiMessages)

        // Restore citation sources for assistant messages
        const restoredSources: Record<string, CitationSource[]> = {}
        for (const msg of detail.messages) {
          if (
            msg.role === AdviserRole.Assistant &&
            msg.sources &&
            Array.isArray(msg.sources) &&
            msg.sources.length > 0
          ) {
            restoredSources[widgetCallIdOf(msg, splitsText) ?? String(msg.id)] =
              msg.sources
          }
        }
        setSourcesMap(restoredSources)

        // Restore existing feedback votes
        if (detail.feedback) {
          const map: Record<string, FeedbackVote> = {}
          for (const fb of detail.feedback) {
            if (fb.messageId && (fb.rating === 1 || fb.rating === -1)) {
              map[String(fb.messageId)] = {
                rating: fb.rating,
                comment: fb.comment ?? undefined,
              }
            }
          }
          setFeedbackMap(map)
        }
      }
    },
    [
      dispatch,
      stop,
      resetTranscript,
      showConversationNotFound,
      setMessages,
      setSourcesMap,
      setFeedbackMap,
      trackConversationResumed,
      userTurnsRef,
      clearPendingResume,
      havenCaseHistoryEnabled,
    ]
  )

  useEffect(() => {
    if (resumedTurnStored === null) return
    consumeResumedTurn()
    if (resumedTurnStored === activeConversationId)
      restoreConversation(resumedTurnStored, { continuingLaunch: true })
  }, [
    resumedTurnStored,
    consumeResumedTurn,
    activeConversationId,
    restoreConversation,
  ])

  const loadOlderHistory = useCallback(async () => {
    if (
      activeConversationId === null ||
      loadingOlderHistory ||
      (!messagesCursor && !caseEventsCursor)
    ) {
      return
    }
    const restore = latestRestoreRef.current
    setLoadingOlderHistory(true)
    setOlderHistoryError(false)
    const detail = await fetchConversationDetail(activeConversationId, {
      history: 'recent',
      ...(messagesCursor ? { messagesCursor } : {}),
      ...(caseEventsCursor ? { caseEventsCursor } : {}),
    })
    if (restore !== latestRestoreRef.current) return
    setLoadingOlderHistory(false)
    if (!detail?.messagesPage) {
      setOlderHistoryError(true)
      return
    }

    if (messagesCursor) {
      const olderMessages = detail.messages.map(toRestoredUiMessage)
      setMessages((current) => {
        const seen = new Set(current.map((message) => message.id))
        return [
          ...olderMessages.filter((message) => !seen.has(message.id)),
          ...current,
        ]
      })
      setRestoredMessageDetails((current) => ({
        ...Object.fromEntries(
          detail.messages.map((message) => [
            String(message.id),
            {
              role: message.role,
              createdAt: message.createdAt,
              operator: message.operator,
            },
          ])
        ),
        ...current,
      }))
      const olderSources: Record<string, CitationSource[]> = {}
      for (const message of detail.messages) {
        if (
          message.role === AdviserRole.Assistant &&
          message.sources &&
          Array.isArray(message.sources) &&
          message.sources.length > 0
        ) {
          olderSources[
            widgetCallIdOf(message, splitsText) ?? String(message.id)
          ] = message.sources
        }
      }
      setSourcesMap((current) => ({ ...olderSources, ...current }))
      const olderFeedback: Record<string, FeedbackVote> = {}
      for (const feedback of detail.feedback ?? []) {
        if (
          feedback.messageId &&
          (feedback.rating === 1 || feedback.rating === -1)
        ) {
          olderFeedback[String(feedback.messageId)] = {
            rating: feedback.rating,
            comment: feedback.comment ?? undefined,
          }
        }
      }
      setFeedbackMap((current) => ({ ...olderFeedback, ...current }))
      setMessagesCursor(detail.messagesPage.nextCursor)
    }
    if (caseEventsCursor) {
      setCaseEvents((current) => {
        const seen = new Set(current.map((event) => event.id))
        return [
          ...(detail.caseEvents?.items ?? []).filter(
            (event) => !seen.has(event.id)
          ),
          ...current,
        ]
      })
      setCaseEventsCursor(detail.caseEvents?.nextCursor ?? null)
    }
    setHistoryRevision((revision) => revision + 1)
  }, [
    activeConversationId,
    loadingOlderHistory,
    messagesCursor,
    caseEventsCursor,
    setMessages,
    setSourcesMap,
    setFeedbackMap,
  ])

  const handledOperatorAcknowledgementRef = useRef(0)
  useEffect(() => {
    if (
      !operatorAcknowledged ||
      operatorAcknowledged.version === handledOperatorAcknowledgementRef.current
    ) {
      return
    }
    handledOperatorAcknowledgementRef.current = operatorAcknowledged.version
    if (activeConversationId === operatorAcknowledged.conversationId) {
      void restoreConversation(operatorAcknowledged.conversationId, {
        trackResume: false,
      })
    }
  }, [operatorAcknowledged, activeConversationId, restoreConversation])

  const handleSelectConversation = useCallback(
    (id: number) => {
      if (id === activeConversationId) hideHistory()
      else if (onHavenRoute) navigateToConversation(id)
      else restoreConversation(id)
    },
    [
      activeConversationId,
      onHavenRoute,
      hideHistory,
      navigateToConversation,
      restoreConversation,
    ]
  )

  const activeConversationIdRef = useRef(activeConversationId)
  activeConversationIdRef.current = activeConversationId

  // A refresh or a remount loses the transcript; the panel's conversation comes back from Redux or this tab's storage.
  useMountEffect(() => {
    if (onHavenRoute) return
    const saved = readSavedPanelConversation()
    if (saved !== undefined && !panelOpen) dispatch(openPanel())
    const id = activeConversationIdRef.current ?? saved ?? null
    if (id !== null) restoreConversation(id)
  })

  useEffect(() => {
    if (!onHavenRoute)
      savePanelConversation(panelOpen ? activeConversationId : undefined)
  }, [onHavenRoute, panelOpen, activeConversationId])

  // In full screen the URL decides which conversation is open: a link, a refresh, back or forward.
  const handledHavenLocationKeyRef = useRef<string | null>(null)
  useEffect(() => {
    if (!onHavenRoute || handledHavenLocationKeyRef.current === location.key)
      return
    handledHavenLocationKeyRef.current = location.key
    if (urlConversation === INVALID_CONVERSATION_ID) {
      showConversationNotFound()
      navigateToConversation(null, { replace: true })
      return
    }
    if (urlConversation === null) {
      if (isFullScreen && activeConversationIdRef.current !== null) {
        dispatch(startNewConversation())
        resetTranscript()
      }
      return
    }
    if (
      urlConversation === activeConversationIdRef.current &&
      messages.length > 0
    )
      return
    restoreConversation(urlConversation)
  }, [
    dispatch,
    isFullScreen,
    location.key,
    messages.length,
    navigateToConversation,
    onHavenRoute,
    resetTranscript,
    restoreConversation,
    showConversationNotFound,
    urlConversation,
  ])

  // In full screen the open conversation is written back to the URL: a new conversation's id, a delete, a launch.
  const previousActiveIdRef = useRef(activeConversationId)
  useEffect(() => {
    const previous = previousActiveIdRef.current
    previousActiveIdRef.current = activeConversationId
    if (activeConversationId === previous || !onHavenRoute) return
    if (
      activeConversationId !== null &&
      urlConversation !== activeConversationId
    ) {
      navigateToConversation(activeConversationId, { replace: true })
    } else if (
      activeConversationId === null &&
      previous !== null &&
      urlConversation === previous
    ) {
      navigateToConversation(null, { replace: true })
    }
  }, [
    activeConversationId,
    navigateToConversation,
    onHavenRoute,
    urlConversation,
  ])

  const [headerCompact, setHeaderCompact] = useState(false)
  const [panelMounted, setPanelMounted] = useState(panelVisible)

  useEffect(() => {
    if (panelVisible) setPanelMounted(true)
  }, [panelVisible])

  const handleThreadReply = useCallback<SendMessage>(
    (text, options) => {
      if (activeThreadId !== null) trackMessageReplySent(activeThreadId)
      sendMessage(text, options)
    },
    [activeThreadId, trackMessageReplySent, sendMessage]
  )

  const handleLaunchAction = useCallback(
    (action: HavenLaunchAction) =>
      launchHaven({
        context: { type: 'action', action },
        entryPoint: HAVEN_ENTRY_POINT.welcomePrompt,
      }),
    [launchHaven]
  )

  const handleNewConversation = useCallback(() => {
    trackNewConversationClicked()
    if (urlConversation !== null) navigateToConversation(null)
    startNew()
    resetTranscript()
    setConversationNotFound(false)
    chatInputRef.current?.focus()
  }, [
    startNew,
    resetTranscript,
    trackNewConversationClicked,
    urlConversation,
    navigateToConversation,
  ])

  const handleDeleteOpenConversation = useCallback(
    (id: number) => {
      trackDeleteConversationClicked(id)
      remove(id)
      startNew()
      resetTranscript()
    },
    [remove, trackDeleteConversationClicked, startNew, resetTranscript]
  )

  const refreshRelayConversations = useCallback(async (): Promise<void> => {
    if (havenCaseHistoryEnabled) {
      await dispatch(refreshConversationViews())
      return
    }
    if (!loaded) return
    await dispatch(fetchConversations(0, { throwOnError: true }))
  }, [dispatch, havenCaseHistoryEnabled, loaded])

  const refreshActiveConversation = useCallback(async (): Promise<void> => {
    const id = activeConversationIdRef.current
    if (id === null || !havenVisible) return
    const restore = latestRestoreRef.current
    const detail = await fetchConversationDetail(id, { history: 'recent' })
    if (
      restore !== latestRestoreRef.current ||
      id !== activeConversationIdRef.current
    )
      return
    if (!detail) throw new Error('Failed to refresh the active conversation')

    setCaseEvents((current) => {
      const seen = new Set(current.map((event) => event.id))
      const additions = (detail.caseEvents?.items ?? []).filter(
        (event) => !seen.has(event.id)
      )
      if (additions.length === 0) return current
      return [...current, ...additions].sort((left, right) => {
        if (left.at !== right.at) return left.at < right.at ? -1 : 1
        return left.id - right.id
      })
    })
    if (caseEvents.length === 0) {
      setCaseEventsCursor(detail.caseEvents?.nextCursor ?? null)
    }
    setRestoredConversation((current) => {
      const candidate = {
        ...detail.conversation,
        lastVisibleReplyAt: detail.messages.at(-1)?.createdAt ?? null,
      }
      if (current && hasNewerActivity(current, candidate)) return current
      if (
        current &&
        !hasNewerActivity(candidate, current) &&
        !hasDifferentCase(candidate, current) &&
        current.lastVisibleReplyAt === candidate.lastVisibleReplyAt
      ) {
        return current
      }
      return candidate
    })
    if (isStreamingRef.current) return

    // Answering a widget in another tab adds the tool-result to the assistant
    // message that is already on screen here, under the same saved id. The
    // merge below only appends ids it has not seen, so the tool parts of the
    // messages it keeps have to be refreshed from the server too, or the card
    // stays interactive until a reload.
    const savedById = new Map(
      detail.messages.map((message) => [message.id, message])
    )
    const withSavedToolParts = (message: UIMessage): UIMessage => {
      if (isPendingLocalMessage(message.id)) return message
      const savedId = isRestoredUiMessage(message)
        ? Number(message.id)
        : getPersistedIdForClientId(message.id)
      const saved = savedId === undefined ? undefined : savedById.get(savedId)
      if (saved?.role !== AdviserRole.Assistant) return message
      const pending = new Map(
        restoredToolParts(saved).map((part) => [part.toolCallId, part])
      )
      let changed = false
      // A streamed call is a `tool-<name>` part and its saved copy a
      // `dynamic-tool` one, so they are matched by call id, not by type.
      const parts = message.parts.map((part) => {
        if (part.type !== 'dynamic-tool' && !part.type.startsWith('tool-'))
          return part
        if (!('toolCallId' in part)) return part
        const next = pending.get(part.toolCallId)
        if (!next) return part
        pending.delete(part.toolCallId)
        if (
          part.state === next.state &&
          JSON.stringify('output' in part ? part.output : undefined) ===
            JSON.stringify('output' in next ? next.output : undefined)
        )
          return part
        changed = true
        return next
      })
      const added = [...pending.values()]
      if (added.length === 0 && !changed) return message
      return { ...message, parts: [...parts, ...added] }
    }

    setMessages((current) => {
      const localPersistedIds = new Set(
        current
          .map((message) => getPersistedIdForClientId(message.id))
          .filter((id): id is number => id !== undefined)
      )
      const retained = current
        .filter(
          (message) =>
            !(
              isRestoredUiMessage(message) &&
              localPersistedIds.has(Number(message.id))
            )
        )
        .map(withSavedToolParts)
      const seen = new Set(retained.map((message) => message.id))
      const pendingLocalRoles = new Set(
        retained
          .filter(
            (message) =>
              !isRestoredUiMessage(message) &&
              !isLaunchSentinel(message) &&
              (isPendingLocalMessage(message.id) ||
                isUnmappedSettledMessage(message.id)) &&
              getPersistedIdForClientId(message.id) === undefined
          )
          .map((message) => message.role)
      )
      const additions = detail.messages
        .filter(
          (message) =>
            !seen.has(String(message.id)) &&
            !localPersistedIds.has(message.id) &&
            (message.role === AdviserRole.Operator ||
              !pendingLocalRoles.has(toChatRole(message.role)))
        )
        .map(toRestoredUiMessage)
      const ordered = orderRecentMessagesByServer(
        [...retained, ...additions],
        detail.messages,
        getPersistedIdForClientId
      )
      return ordered.length !== current.length ||
        ordered.some((message, index) => message !== current[index])
        ? ordered
        : current
    })
    setRestoredMessageDetails((current) => {
      const additions = detail.messages.filter(
        (message) => !current[String(message.id)]
      )
      if (additions.length === 0) return current
      return {
        ...current,
        ...Object.fromEntries(
          additions.map((message) => [
            String(message.id),
            {
              role: message.role,
              createdAt: message.createdAt,
              operator: message.operator,
            },
          ])
        ),
      }
    })
    const newSources: Record<string, CitationSource[]> = {}
    for (const message of detail.messages) {
      if (message.role !== AdviserRole.Assistant || !message.sources?.length)
        continue
      newSources[widgetCallIdOf(message, splitsText) ?? String(message.id)] =
        message.sources
    }
    if (Object.keys(newSources).length > 0) {
      setSourcesMap((current) => ({ ...current, ...newSources }))
    }
  }, [
    caseEvents.length,
    getPersistedIdForClientId,
    isPendingLocalMessage,
    isUnmappedSettledMessage,
    havenVisible,
    setMessages,
    setSourcesMap,
  ])

  const listedConversation = conversations.find(
    (conversation) => conversation.id === activeConversationId
  )
  const listedIsNewer =
    listedConversation &&
    restoredConversation?.id === activeConversationId &&
    listedConversation.updatedAt > restoredConversation.updatedAt
  const conversationCandidates = havenCaseHistoryEnabled
    ? [
        ...(listedIsNewer ? [listedConversation] : []),
        restoredConversation,
        ...conversations,
        startedConversation,
      ]
    : [...conversations, restoredConversation, startedConversation]
  const activeConversation = conversationCandidates.find(
    (conversation) => conversation?.id === activeConversationId
  )

  useHavenRelay(
    Boolean(currentUser) && havenCaseHistoryEnabled && havenVisible,
    currentUser?.id,
    Boolean(
      activeConversation?.case &&
      activeConversation.case.status !== RESOLVED_CASE_STATUS
    ),
    {
      refreshConversations: refreshRelayConversations,
      refreshActiveConversation,
    }
  )

  // A new conversation gets its title on the server, so the rail refetches after each reply.
  const wasStreamingRef = useRef(isStreaming)
  useEffect(() => {
    if (wasStreamingRef.current && !isStreaming) {
      if (havenCaseHistoryEnabled) dispatch(refreshConversationViews())
      else refresh()
    }
    wasStreamingRef.current = isStreaming
  }, [dispatch, havenCaseHistoryEnabled, isStreaming, refresh])

  const chatViewMessages = messages
    .filter((msg) => !isLaunchSentinel(msg))
    .filter(
      (msg): msg is UIMessage & { role: 'user' | 'assistant' } =>
        msg.role === AdviserRole.User || msg.role === AdviserRole.Assistant
    )
    .map((msg) => {
      const restored = restoredMessageDetails[msg.id]
      const toolInvocations = getToolInvocations(msg)
      const split = splitTextAroundWidget(msg, splitsDrawnText)
      return {
        id: msg.id,
        saved: isRestoredUiMessage(msg),
        role:
          havenCaseHistoryEnabled && restored?.role === AdviserRole.Operator
            ? AdviserRole.Operator
            : msg.role,
        createdAt:
          restored?.createdAt ??
          (havenCaseHistoryEnabled
            ? messageCreatedAtByClientId[msg.id]
            : undefined),
        operator: restored?.operator,
        content:
          split.after.length > 0 || Object.keys(split.beforeWidget).length > 0
            ? split.before
            : getMessageContent(msg, {
                dedupeRepeatedText: toolInvocations.some(
                  ({ toolName }) => toolName === AdviserToolName.HandToSupport
                ),
              }),
        textBeforeWidget: split.beforeWidget,
        sourcesBeforeWidget: sourcesByWidget(toolInvocations, sourcesMap),
        trailingContent: split.after,
        sources: sourcesMap[msg.id],
        toolInvocations:
          toolInvocations.length > 0 ? toolInvocations : undefined,
        attachedFiles: getAttachedFiles(msg),
      }
    })

  const threadOverlayProps = {
    activeThreadId,
    onBack: () => dispatch(closeMessageThread()),
    onSend: handleThreadReply,
    sendDisabled: isStreaming,
  }

  const transcriptEmpty =
    chatViewMessages.length === 0 &&
    (!havenCaseHistoryEnabled || caseEvents.length === 0) &&
    !isStreaming &&
    !error
  const showNotFound = conversationNotFound && transcriptEmpty
  const inputInWelcome = transcriptEmpty && !showNotFound

  // The API's holder is whoever a plain message reaches, by the same rule the
  // server routes with, so the widget names it rather than inferring one.
  const operatorHeld =
    havenCaseHistoryEnabled && Boolean(activeConversation?.holder)
  const activeCase = havenCaseHistoryEnabled
    ? (activeConversation?.case ?? null)
    : null
  const holderName = activeConversation?.holder?.friendlyName ?? 'Heard team'
  let inputPlaceholder: string | undefined
  if (operatorHeld) {
    inputPlaceholder = `Message ${holderName}, or mention @Haven...`
  } else if (activeCase?.status === RESOLVED_CASE_STATUS) {
    inputPlaceholder = 'Ask Haven, or ask to contact Heard support...'
  }
  // The send path needs the recipient the API picked, and it is only known
  // here, after the active conversation is resolved.
  operatorHeldRef.current = operatorHeld
  const recipientHint = operatorHeld
    ? 'Mention @Haven anywhere for quick answers'
    : undefined
  // A case Haven has back, or one already resolved, leaves nobody to escalate to.
  const caseIsLive = Boolean(
    activeCase &&
    activeCase.status !== RESOLVED_CASE_STATUS &&
    !activeCase.returnedToHaven
  )
  const composerStatus = havenCaseHistoryEnabled ? (
    <ComposerOwnerBar
      holder={activeConversation?.holder ?? null}
      caseIsLive={caseIsLive}
      waitingOnCustomer={activeCase?.waitingOn === 'customer'}
      disabled={isStreaming || restoring}
      onAskPerson={() => sendMessage(MESSAGE_TEAM_CHIP)}
    />
  ) : undefined
  const conversationHeader = (
    <ConversationHeader
      conversation={activeConversation}
      caseEvents={caseEvents}
      caseHistoryEnabled={havenCaseHistoryEnabled}
      stacked={!isFullScreen}
      onDelete={handleDeleteOpenConversation}
    />
  )
  const chatIntro =
    activeLaunchAction === HAVEN_LAUNCH_ACTION.openTickets &&
    activeEntryPoint === HAVEN_ENTRY_POINT.sidebarMessages ? (
      <MessagesLaunchCard />
    ) : undefined
  const chatView = (
    <ChatView
      key={restoreCount}
      messages={chatViewMessages}
      conversationId={activeConversationId}
      caseEvents={
        havenCaseHistoryEnabled && activeConversation?.case ? caseEvents : []
      }
      hasOlderHistory={Boolean(messagesCursor || caseEventsCursor)}
      hasOlderMessages={Boolean(messagesCursor)}
      loadingOlderHistory={loadingOlderHistory}
      olderHistoryError={olderHistoryError}
      historyRevision={historyRevision}
      onLoadOlderHistory={loadOlderHistory}
      showAssistantAuthor={Boolean(
        havenCaseHistoryEnabled && activeConversation?.case
      )}
      isStreaming={isStreaming}
      inputDisabled={restoring}
      inputPlaceholder={inputPlaceholder}
      recipientHint={recipientHint}
      error={error}
      firstName={currentUser?.firstName}
      intro={chatIntro}
      messagesLaunch={activeLaunchAction === HAVEN_LAUNCH_ACTION.openTickets}
      composerStatus={composerStatus}
      onLaunchAction={handleLaunchAction}
      messagesViewEnabled={havenMessagesViewEnabled}
      feedbackMap={feedbackMap}
      hideInput={!isFullScreen && !inputInWelcome}
      inputRef={chatInputRef}
      onSend={sendMessage}
      onRetry={retry}
      onWidgetOutcome={submitWidgetOutcome}
      onScrolledChange={setHeaderCompact}
      contentClassName={
        isFullScreen ? 'mx-auto w-full max-w-[800px]' : undefined
      }
    />
  )
  const renderHistoryRail = (onNewConversation?: () => void) =>
    havenCaseHistoryEnabled ? (
      <ConversationRail
        activeConversationId={activeConversationId}
        onSelect={handleSelectConversation}
        onNewConversation={onNewConversation}
      />
    ) : (
      <HistoryRail
        conversations={conversations}
        activeConversationId={activeConversationId}
        loading={!loaded}
        hasMore={hasMore}
        onSelect={handleSelectConversation}
        onLoadMore={loadMore}
        onNewConversation={onNewConversation}
      />
    )

  if (isFullScreen) {
    let fullScreenContent: ReactNode
    if (showNotFound) {
      fullScreenContent = (
        <ConversationNotFound onNewConversation={handleNewConversation} />
      )
    } else if (transcriptEmpty) {
      fullScreenContent = (
        <FullScreenHome
          firstName={currentUser?.firstName}
          onSend={sendMessage}
          inputDisabled={restoring}
          onLaunchAction={handleLaunchAction}
        />
      )
    }
    return (
      <>
        <HavenFullScreen
          rail={renderHistoryRail(handleNewConversation)}
          onExitFullScreen={handleExitFullScreen}
          onClose={handleClosePanel}
        >
          <ThreadOverlay {...threadOverlayProps}>
            {fullScreenContent ?? (
              <div className="flex flex-col flex-1 min-h-0">
                {conversationHeader}
                <div className="flex flex-col flex-1 min-h-0 w-full">
                  {chatView}
                </div>
              </div>
            )}
          </ThreadOverlay>
        </HavenFullScreen>
        <ChatPlaidLauncher />
      </>
    )
  }

  let panelContent: ReactNode
  if (activeView === 'history') {
    panelContent = (
      <div className="flex flex-col flex-1 min-h-0 px-6 pt-6 bg-neutral-0">
        {renderHistoryRail()}
      </div>
    )
  } else if (showNotFound) {
    panelContent = (
      <ConversationNotFound onNewConversation={handleNewConversation} />
    )
  } else {
    panelContent = (
      <div className="flex flex-col flex-1 min-h-0">
        {!transcriptEmpty && conversationHeader}
        {chatView}
      </div>
    )
  }

  if (!panelMounted) {
    // Portal to document.body so the trigger is never clipped by the layout's
    // overflow:hidden or any ancestor stacking context (critical on mobile).
    return createPortal(
      <ChatTrigger
        onClick={handleTogglePanel}
        isThinking={isStreaming}
        isNewUser={isNewUserForSidebarRestructure(currentUser?.createdAt)}
        awaitingCount={awaitingCount}
      />,
      document.body
    )
  }

  return (
    <div
      className={mtc('h-full flex-shrink-0 bg-neutral-0', isMobile && 'w-0')}
    >
      <ChatPanel
        open={panelVisible}
        onClosed={() => setPanelMounted(false)}
        title={HAVEN_TITLE}
        compact={headerCompact}
        onClose={handleClosePanel}
        onExpand={isMobile ? undefined : handleExpand}
        onSetView={handleSetView}
        onNewConversation={handleNewConversation}
        inputArea={
          activeView === 'chat' &&
          activeThreadId === null &&
          !inputInWelcome ? (
            <>
              {composerStatus}
              <ChatInput
                ref={chatInputRef}
                onSend={sendMessage}
                placeholder={inputPlaceholder}
                recipientHint={recipientHint}
                disabled={isStreaming || restoring}
                initialFocus
              />
            </>
          ) : undefined
        }
      >
        <ThreadOverlay {...threadOverlayProps}>{panelContent}</ThreadOverlay>
      </ChatPanel>
      <ChatPlaidLauncher />
    </div>
  )
}
